/**
 * OrderCartContainer
 * --
 */
import React from 'react';
import OrderCartPresenter from './OrderCartPresenter';
import {
  useDefaultShopDataStack,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, {
  extractApplicationCommission,
  extractApplicationPolicy,
  findGetRootData,
  STORE_URL,
} from 'utils';
import lodash from 'utils/Lodash';
import {
  useBulkDeleteCart,
  useUpdateCart,
  useUpsertBulkCart,
} from 'hooks/CartHooks';
import { useCreateWishlist, useDeleteWishlist } from 'hooks/WishlistHooks';
import dayjs from 'dayjs';
import { useGetForwardingLocations } from 'hooks/ForwardingLocationsHooks';

const OrderCartContainer = (props) => {
  /* ===== ROUTE ===== */

  /* ===== Mutate ===== */
  const { upsertBulkCart } = useUpsertBulkCart();
  const { bulkDeleteCart } = useBulkDeleteCart();
  const { updateCart } = useUpdateCart();
  const { createWishlist } = useCreateWishlist();
  const { deleteWishlist } = useDeleteWishlist();

  /* ===== Store ===== */
  const { products, policies, shops, promotion, categories } =
    useDefaultShopDataStack();
  const { cartList, wishlist } = useUserDataStack();
  const userDataAction = useUserDataAction();
  const userInfo = useUserStack();

  /* ===== PROPS ===== */
  const { navigate, searchParams, screenSize } = props;

  const type = React.useMemo(() => {
    return searchParams.get('type') ?? 'delivery';
  }, [searchParams]);

  /* ===== Data ===== */
  // 출고지 데이터 호출
  const { data: Forwarding } = useGetForwardingLocations(null, {
    enabled: type === 'store_pickup' ? true : false,
    initialData: [],
    select: (res) => {
      if (res.status !== 200) return [];

      // 픽업지만 추출
      const receive = res.data.filter(
        (forwarding) => forwarding.type === 'RECEIVE'
      );

      return receive;
    },
  });

  /* ===== STATE ===== */
  // 선택 상품 목록
  const [selectedProduct, setSelectedProduct] = React.useState({});
  // 상품 옵션, 수량 변경 데이터
  const [modalData, setModalData] = React.useState(null);
  // 카트 업데이트 데이터
  const [updateCartData, setUpdateCartData] = React.useState([]);

  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 상품 선택
   * --
   * @param {string} key
   * @param {object} d
   */
  const handleCheckedList = (key, d) => {
    const keyItem = selectedProduct[key];

    if (keyItem) {
      const isExist = keyItem.findIndex((item) => item.key === d.key);

      if (isExist === -1) {
        setSelectedProduct((prev) => ({
          ...prev,
          [key]: [...prev[key], d],
        }));
      } else {
        setSelectedProduct((prev) => ({
          ...prev,
          [key]: prev[key].filter((item) => item.key !== d.key),
        }));
      }
    } else {
      setSelectedProduct((prev) => ({
        ...prev,
        [key]: [d],
      }));
    }
  };

  /**
   * 상품 전체 선택
   * --
   * @param {string} key
   * @param {array} list
   */
  const handleCheckedListAll = (key, list) => {
    const keyItem = selectedProduct[key];

    if (!keyItem) {
      setSelectedProduct((prev) => ({
        ...prev,
        [key]: list,
      }));
    } else if (keyItem.length === list.length) {
      setSelectedProduct((prev) => ({
        ...prev,
        [key]: [],
      }));
    } else {
      setSelectedProduct((prev) => ({
        ...prev,
        [key]: list,
      }));
    }
  };

  /**
   * 장바구니 수량 업데이트
   * --
   */
  const handleCartNumber = (state, quantity) => {
    const { data, indexKey, key } = state;

    const fid = cartList.findIndex((item) => item.cart_id === data.cart_id);

    userDataAction.fetchData('cartList', fid, { cart_num: quantity });

    setSelectedProduct((prev) => {
      const selectedProductList = prev[indexKey] || [];
      return {
        ...prev,
        [indexKey]: selectedProductList.map((item) =>
          item.key === key
            ? {
                ...item,
                num: quantity,
                data: { ...item.data, cart_num: quantity },
              }
            : item
        ),
      };
    });

    // NOTE: 장바구니 데이터 업데이트할 배열
    const newData = {
      cart_id: data.cart_id,
      cart_num: quantity,
    };

    // updateCartData에서 cart_id를 기준으로 항목 찾기
    const findIdx = updateCartData.findIndex(
      (item) => item.cart_id === newData.cart_id
    );

    if (findIdx !== -1) {
      // 이미 존재하는 경우 업데이트
      setUpdateCartData((prev) =>
        prev.map((item, index) =>
          index === findIdx ? { ...item, ...newData } : item
        )
      );
    } else {
      // 없는 경우 추가
      setUpdateCartData((prev) => [...prev, newData]);
    }
  };

  /**
   * 장바구니 데이터 수량 업데이트
   * --
   */
  // eslint-disable-next-line
  const handleUpdateCartNumber = React.useCallback(
    lodash.debounce(async (list) => {
      try {
        const res = await upsertBulkCart(list);

        if (res.status !== 200) {
          MessageAlert.warning('장바구니 수량 업데이트에 실패하였습니다');
          return;
        }

        setUpdateCartData([]);
      } catch (err) {
        console.error(`[CartNumber] ERROR!! `, err);
        return;
      }
    }, 2000),
    []
  );

  /**
   * 장바구니 데이터 삭제
   * --
   * @param {string} t
   * @returns
   */
  const handleRemoveCart = (t = 'select', idx) => {
    const removeType = t.toLocaleUpperCase();
    try {
      const confirm = (msg) => {
        return window.confirm(msg);
      };

      // NOTE: 장바구니 삭제 API
      const remove = async (list) => {
        const res = await bulkDeleteCart(list);

        if (res.status !== 200) {
          MessageAlert.warning('장바구니에서 삭제하는데 실패하였습니다.');
          console.error(res.message);
          return;
        }

        const removeData = cartList.filter(
          (cart) => !list.some((item) => item.cart_id === cart.cart_id)
        );

        userDataAction.setData('cartList', removeData);
        MessageAlert.success(
          `${list.length}개의 상품이 장바구니에서 삭제되었습니다.`
        );
        setSelectedProduct({});
      };

      switch (removeType) {
        case 'SELECT':
          const selected = Object.values(selectedProduct);

          if (!selected.length) {
            MessageAlert.info('삭제하실 상품을 선택해주세요!');
            return;
          }

          if (!confirm('선택하신 상품을 장바구니에서 삭제하시겠습니까?'))
            return;

          const removeData = selected.reduce((acc, cur) => {
            const rData = cur.map((item) => {
              const { data } = item;

              return {
                cart_id: data.cart_id,
              };
            });
            acc = acc.concat(rData);

            return acc;
          }, []);

          remove(removeData);
          break;

        case 'ALL':
          if (!confirm('장바구니에 담긴 상품을 모두 삭제하시겠습니까?')) return;

          const allData = cartList.map((cart) => ({ cart_id: cart.cart_id }));

          remove(allData);
          break;

        case 'SINGLE':
          if (!confirm('상품을 장바구니에서 삭제하시겠습니까?')) return;
          const singleData = [{ cart_id: idx }];

          remove(singleData);
          break;

        default:
          MessageAlert.warning('장바구니에서 삭제하는데 실패하였습니다.');
          break;
      }
    } catch (err) {
      console.error('[handleRemoveCart] Error!! ', err);
      MessageAlert.error('장바구니에서 제거하는데 실패하였습니다!');
      return;
    }
  };

  /**
   * 주문하기
   * --
   */
  const handleOrder = () => {
    const selected = Object.values(selectedProduct);

    if (!selected.length) {
      MessageAlert.info('구매하실 상품을 선택해주세요!');
      return;
    }

    const orderData = selected.reduce((acc, cur) => {
      const orData = cur.map((item) => {
        const { data } = item;

        return data;
      });
      acc = acc.concat(orData);

      return acc;
    }, []);

    userDataAction.setData('shopOrderList', orderData);

    navigate(`/order/order?type=${type}`);
  };

  /**
   * 장바구니 찜/고정
   * --
   */
  const handleUpdateCart = async (type = 'wish', target) => {
    const updateType = type.toLocaleUpperCase();
    try {
      const confirm = (t, a = '') =>
        window.confirm(
          `선택하신 상품을 ${t}${a ? ` ${a} ` : ''} 하시겠습니까?`
        );

      switch (updateType) {
        case 'WISH_ADD':
          if (!confirm('찜목록에 추가')) return;

          const createWish = {
            user_id: userInfo.user_id,
            product_id: target,
          };

          const { status: createWishStatus, data: createWishData } =
            await createWishlist(createWish);

          if (createWishStatus !== 200) {
            MessageAlert.warning('찜하기에 실패하였습니다.');
            return;
          }

          userDataAction.addData('wishlist', createWishData);
          MessageAlert.success('상품이 찜목록에서 추가되었습니다!');
          break;
        case 'WISH_DELETE':
          if (!confirm('찜목록에서', '삭제')) return;

          const { status: deleteWishStatus } = await deleteWishlist(target);

          if (deleteWishStatus !== 200) {
            MessageAlert.warning('찜 해지에 실패하였습니다.');
            return;
          }

          const newWish = wishlist.filter(
            (wish) => wish.wishlist_id !== target
          );

          userDataAction.setData('wishlist', newWish);
          MessageAlert.success('상품이 찜목록에서 삭제되었습니다!');
          break;
        case 'FIX_ADD':
        case 'FIX_DELETE':
          if (
            !confirm('장바구니에 고정', updateType === 'FIX_ADD' ? '' : '해지')
          )
            return;

          const fixData = {
            cart_id: target,
            is_fixed: updateType === 'FIX_ADD' ? 'Y' : 'N',
          };

          const res = await updateCart(fixData);

          if (res.status !== 200) {
            MessageAlert.info(
              `장바구니 고정${
                updateType === 'FIX_ADD' ? '' : ' 해지 '
              }에 실패하였습니다!`
            );
            return;
          }

          const newCartData = cartList.map((cart) =>
            cart.cart_id === target ? res.data : cart
          );

          userDataAction.setData('cartList', newCartData);
          MessageAlert.success(
            `상품이 장바구니에 ${
              updateType === 'FIX_ADD' ? '고정' : '고정 해지'
            }되었습니다.`
          );

          break;
        default:
          MessageAlert.info(
            `${updateType === 'WISH' ? '찜하기' : '고정'}에 실패하였습니다!`
          );
          return;
      }
    } catch (err) {
      MessageAlert.error(
        `${updateType === 'WISH' ? '찜하기' : '고정'}에 실패하였습니다!`
      );
      console.error('[handleUpdateCart] Error!! ', err);
      return;
    }
  };

  /* ===== HOOKS ===== */
  /**
   * 진행중인 프로모션 추출
   * --
   */
  const ProgressPromotion = React.useMemo(() => {
    if (!promotion || !promotion.length) return [];

    const today = dayjs().unix();

    return promotion.filter(
      (pr) => pr.promotion_start_date <= today && pr.promotion_end_date >= today
    );
  }, [promotion]);

  /**
   * 장바구니 + 상품 아이템 정제
   * --
   */
  const CartList = React.useMemo(() => {
    const newCartList = cartList.map((cart) => {
      const findProduct = products.find(
        (product) => product.product_id === cart.product_id
      );

      if (!findProduct)
        return { DELIVERY: [], STORE_PICKUP: [], RENTAL: [], SUBSCRIPTION: [] };

      const { forwarding_location_id } = cart;
      const {
        product_option,
        shop_information_id,
        product_file,
        product_id,
        price,
        num,
        product_status,
        forwarding_location,
        ...rest
      } = findProduct;

      // 출고지
      const forwardingLocation =
        type === 'store_pickup'
          ? Forwarding.find(
              (forwarding) =>
                forwarding.forwarding_location_id === forwarding_location_id
            )
          : forwarding_location;

      // 품절여부
      const isSoldOut = product_status === 'SOLDOUT' || num === 0 ? 'Y' : 'N';

      // 썸네일 추출
      const thumbnail =
        product_file.find((file) => file.type === 'THUMBNAIL') || null;

      // 쇼핑몰 추출
      const shopData = shops.find(
        (shop) => shop.shop_information_id === shop_information_id
      );

      // 옵션 금액
      const optionPrice =
        cart.product_option?.reduce((price, option) => {
          const op = option.option.reduce((acc, cur) => {
            return acc + (Number(cur.option_price) ?? 0);
          }, 0);

          return (price += op);
        }, 0) ?? 0;

      // 적용 정책 추출
      const applyPolicy = policies.filter((policy) =>
        policy.map_product_policy.some((map) => map.product_id === product_id)
      );
      // 우선순위에 따른 적용되는 정책 추출
      const applicationPolicy = applyPolicy.length
        ? extractApplicationPolicy(
            applyPolicy,
            price + optionPrice,
            ProgressPromotion
          )
        : null;
      // 적용 상품 목록 제거
      const restPolicy = applicationPolicy
        ? (() => {
            delete applicationPolicy['map_product_policy'];
            return applicationPolicy;
          })()
        : null;

      // 최상위 카테고리 추출
      const rootCategory = findGetRootData(
        categories,
        rest.product_category_id
      );

      // 적용 수수료
      const commission = applyPolicy
        .filter((policy) => {
          if (policy.policy_type === 'PROMOTION') {
            return ProgressPromotion.some(
              (pr) => pr.promotion_id === policy.promotion_id
            );
          }

          return true;
        })
        .map((policy) => {
          const { commission_rate, policy_type } = policy;

          return {
            type: policy_type,
            commission_rate: commission_rate,
          };
        });
      const commissionList = [
        ...commission,
        { type: 'BRAND', commission_rate: shopData?.commission_rate },
        { type: 'CATEGORY', commission_rate: rootCategory?.commission_rate },
      ];
      // 적용된 수수료 추출
      const applyCommission = extractApplicationCommission(commissionList);

      return {
        ...cart,
        src: !thumbnail ? null : `${STORE_URL}/${thumbnail.file_url}`,
        discount_rate: applicationPolicy ? applicationPolicy.discount_rate : 0,
        discount_type: applicationPolicy
          ? applicationPolicy.discount_type
          : 'W',
        reserve_rate: applicationPolicy ? applicationPolicy.reserve_rate : 0,
        reserve_type: applicationPolicy ? applicationPolicy.reserve_type : 'W',
        finalPrice: applicationPolicy
          ? price + optionPrice - applicationPolicy.discount_price
          : price + optionPrice,
        isWish: wishlist.find((wish) => wish.product_id === product_id)
          ?.wishlist_id,
        options: product_option,
        product_status,
        ...rest,
        brand: shopData,
        price: price,
        policy: restPolicy,
        option_price: optionPrice,
        is_sold_out: isSoldOut,
        forwarding_location: forwardingLocation,

        commission: applyCommission,
      };
    });

    return newCartList;
  }, [
    products,
    cartList,
    policies,
    shops,
    wishlist,
    ProgressPromotion,
    categories,
    Forwarding,
    type,
  ]);

  /**
   * 타입 변경 시 장바구니 초기화 처리
   * --
   */
  React.useEffect(() => {
    setSelectedProduct({});
  }, [type]);

  /**
   * 장바구니 수량 업데이트
   * --
   */
  React.useEffect(() => {
    if (updateCartData.length) {
      handleUpdateCartNumber(updateCartData);
    }
    // eslint-disable-next-line
  }, [updateCartData]);

  /* ===== RENDER ===== */
  return (
    <>
      <OrderCartPresenter
        navigate={navigate}
        type={type.toLocaleUpperCase()}
        cart={CartList}
        selectedProduct={selectedProduct}
        onCheckedList={handleCheckedList}
        onCheckedListAll={handleCheckedListAll}
        modalData={modalData}
        setModalData={setModalData}
        onCartNumber={handleCartNumber}
        onRemoveCart={handleRemoveCart}
        onOrder={handleOrder}
        onUpdateCart={handleUpdateCart}
        screenSize={screenSize}
      />
    </>
  );
};

export default OrderCartContainer;
